import React, {FC, ReactNode, useState} from 'react';
import styled from 'styled-components';

import {Handler, HandlerOf} from '../../../libs/shared/helpers/src/react/reactHelpers';
import {useTimeout} from '../../../srcCommon/helpers/hookHelpers';
import {
  SettingsCustomFieldsPagesEnum,
  SettingsCustomFieldsUrl,
  SettingsResourcePagesEnum,
} from '../../models/url/settingsUrlModel';
import {SettingsBanner} from '../banners/settingsBanner';
import {SettingsBannersEnum} from '../banners/settingsBannersContext';
import {EditCustomFieldFormContainer} from './form/editCustomFieldFormContainer';
import {NewCustomFieldForm} from './form/newCustomFieldForm';
import {SettingsCustomFieldsList} from './list/settingsCustomFieldsList';

/*
 * Props.
 */

interface SettingsCustomFieldsProps {
  url: SettingsCustomFieldsUrl;
  isInfoBoxExpanded: boolean;
  toggleInfoBox: Handler;
}

/*
 * Style.
 */

const StyledBanner = styled.div`
  position: absolute;
  top: 30px;
  left: 0;
  width: 100%;
`;

/*
 * Component.
 */

export const SettingsCustomFields: FC<SettingsCustomFieldsProps> = (props) => {
  const {
    url: {customFieldId, resourcePage, tabPage},
    isInfoBoxExpanded,
    toggleInfoBox,
  } = props;

  const [errorBannerMessage, setErrorBannerMessage] = useState<ReactNode>(null);
  const [successBannerMessage, setSuccessBannerMessage] = useState<ReactNode>(null);
  const [setTimeout] = useTimeout();

  const handleSuccessMessage = (message: ReactNode) => {
    setSuccessBannerMessage(message);
    setTimeout(() => setSuccessBannerMessage(null), 3000);
  };

  return (
    <>
      {maybeRenderPage(
        resourcePage,
        tabPage,
        setErrorBannerMessage,
        handleSuccessMessage,
        isInfoBoxExpanded,
        toggleInfoBox,
        customFieldId,
      )}
      {maybeRenderErrorBanner(errorBannerMessage, setErrorBannerMessage)}
      {maybeRenderSuccessBanner(successBannerMessage, setSuccessBannerMessage)}
    </>
  );
};

/*
 * Helpers.
 */

function maybeRenderPage(
  resourcePage: SettingsResourcePagesEnum,
  tabPage: SettingsCustomFieldsPagesEnum,
  setErrorBannerMessage: HandlerOf<ReactNode>,
  setSuccessBannerMessage: HandlerOf<ReactNode>,
  isSidebarOpen: boolean,
  toggleSidebar: Handler,
  customFieldId?: number,
) {
  if (resourcePage === SettingsResourcePagesEnum.EDIT && customFieldId) {
    return (
      <EditCustomFieldFormContainer
        key={tabPage}
        tabPage={tabPage}
        customFieldId={customFieldId}
        setErrorBannerMessage={setErrorBannerMessage}
        setSuccessBannerMessage={setSuccessBannerMessage}
      />
    );
  }

  if (resourcePage === SettingsResourcePagesEnum.NEW) {
    return (
      <NewCustomFieldForm
        key={tabPage}
        tabPage={tabPage}
        setErrorBannerMessage={setErrorBannerMessage}
        setSuccessBannerMessage={setSuccessBannerMessage}
      />
    );
  }

  if (resourcePage === SettingsResourcePagesEnum.OVERVIEW) {
    return (
      <SettingsCustomFieldsList
        key={tabPage}
        tabPage={tabPage}
        isSidebarOpen={isSidebarOpen}
        toggleSidebar={toggleSidebar}
        setSuccessBannerMessage={setSuccessBannerMessage}
      />
    );
  }

  return null;
}

function maybeRenderErrorBanner(errorBannerMessage: ReactNode, setErrorBannerMessage: HandlerOf<ReactNode>) {
  if (!errorBannerMessage) {
    return null;
  }

  return (
    <StyledBanner>
      <SettingsBanner type={SettingsBannersEnum.ERROR} onDismissClick={() => setErrorBannerMessage(null)}>
        {errorBannerMessage}
      </SettingsBanner>
    </StyledBanner>
  );
}

function maybeRenderSuccessBanner(
  successBannerMessage: ReactNode,
  setSuccessBannerMessage: HandlerOf<ReactNode>,
) {
  if (!successBannerMessage) {
    return null;
  }

  return (
    <StyledBanner>
      <SettingsBanner type={SettingsBannersEnum.SUCCESS} onDismissClick={() => setSuccessBannerMessage(null)}>
        {successBannerMessage}
      </SettingsBanner>
    </StyledBanner>
  );
}
